import { MAX_ID_LENGTH, isValidId } from '../songpack/index.js';

/**
 * Song Pack identifiers.
 *
 * `packId` is ASCII-only by format rule — Unity's `ValidateId`, the Python
 * engine's `write_song_pack` and this runtime's manifest parser all enforce the
 * same charset — while `displayName` is free text. On this project that gap is
 * not theoretical: the songs are called things like `独身税パレード♡`, and
 * slugging that into an identifier would produce either an empty string or a
 * transliteration nobody asked for.
 *
 * So the identifier is not derived from the title at all. It is a timestamp, in
 * exactly the shape Unity's CREATE SONG already produces:
 *
 *     song_20260814_113045_207
 *
 * The title lives in `displayName`, verbatim, which is where the format says it
 * belongs and what Mirror Drift resolves `<displayName>.mp4` from. Two packs
 * made from the same song get different ids and the same name, which is correct:
 * they are different packs.
 */

/** Unity's prefix, kept so packs from both tools sort and read alike. */
export const PACK_ID_PREFIX = 'song_';

/** The charset every producer and reader of Song Pack v1 agrees on. */
const ALLOWED = /[^A-Za-z0-9_.-]/g;

/**
 * A fresh identifier for a new pack.
 *
 * Millisecond resolution matches Unity's `yyyyMMdd_HHmmss_fff`. Two packs made
 * inside the same millisecond would collide, which is why `uniquePackId` exists
 * for callers that hold a set of ids already in use.
 */
export function derivePackId(now: Date = new Date()): string {
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');
  return (
    PACK_ID_PREFIX +
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}_` +
    pad(now.getMilliseconds(), 3)
  );
}

/** `derivePackId`, then a `_2`, `_3`… suffix until nothing collides. */
export function uniquePackId(taken: ReadonlySet<string>, now: Date = new Date()): string {
  const base = derivePackId(now);
  if (!taken.has(base)) return base;
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}_${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Forces an arbitrary string into the identifier charset.
 *
 * For the case where a pack id comes from somewhere other than `derivePackId` —
 * a re-import, or a future screen that lets one be typed. Returns an empty
 * string when nothing usable survives, which the caller must treat as "no id"
 * rather than substituting one silently.
 */
export function sanitisePackId(value: string): string {
  const cleaned = value.replace(ALLOWED, '_').replace(/_{2,}/g, '_').slice(0, MAX_ID_LENGTH);
  // A leading or trailing separator is legal but reads as damage.
  const trimmed = cleaned.replace(/^[_.-]+/, '').replace(/[_.-]+$/, '');
  return isValidId(trimmed) ? trimmed : '';
}
